import {
  type DocumentNode,
  type OperationVariables,
  ApolloClient,
  HttpLink,
  InMemoryCache,
} from '@apollo/client/core'
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev'
import {
  createStorefrontClient,
  getShopifyCookies,
  SHOPIFY_S,
  SHOPIFY_STOREFRONT_ID_HEADER,
  SHOPIFY_STOREFRONT_S_HEADER,
  SHOPIFY_STOREFRONT_Y_HEADER,
  SHOPIFY_Y,
} from '@shopify/hydrogen-react'

import { getLocaleVariable } from '@lib/environment'
import { type Locale } from '@lib/language'
import {
  getLocaleFromShopifyStoreDomain,
  getShopifyStoreDomainFromLocales,
} from '../helpers'

export type ShopifyClient = ApolloClient

export const shopifyApiVersion = '2025-04'

if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
  // Adds Apollo messages only in a dev environment
  loadDevMessages()
  loadErrorMessages()
}

/**
 * Returns Apollo client for Shopify Storefront GraphQL API.
 */
export const getShopifyStorefrontGraphQlClient = (locale: Locale) => {
  const shopifyStoreDomain = getShopifyStoreDomainFromLocales(locale)
  const shopifyStorefrontAccessToken = getLocaleVariable(
    locale,
    'NEXT_PUBLIC_SHOPIFY_API_TOKEN',
  )

  if (!shopifyStorefrontAccessToken) {
    throw new Error('Shopify storefront token missing')
  }

  const storefrontClient = createStorefrontClient({
    storeDomain: shopifyStoreDomain,
    publicStorefrontToken: shopifyStorefrontAccessToken,
    storefrontApiVersion: shopifyApiVersion,
    contentType: 'json',
  })

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: new HttpLink({
      uri: storefrontClient.getStorefrontApiUrl(),
      headers: storefrontClient.getPublicTokenHeaders(),
    }),
  })
}

/**
 * Returns Apollo client for Shopify Admin GraphQL API.
 */
export const getShopifyAdminGraphQlClient = (
  shopifyStoreDomain?: string,
  locale?: Locale,
) => {
  if (!shopifyStoreDomain) {
    throw new Error('Shopify store domain missing')
  }

  const normalizedLocale =
    locale ?? getLocaleFromShopifyStoreDomain(shopifyStoreDomain)
  const shopifyAdminAccessToken = getLocaleVariable(
    normalizedLocale,
    'SHOPIFY_API_PASSWORD',
  )

  if (!shopifyAdminAccessToken) {
    throw new Error('Shopify admin token missing')
  }

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: new HttpLink({
      uri: `https://${shopifyStoreDomain}/admin/api/${shopifyApiVersion}/graphql.json`,
      headers: {
        'X-Shopify-Access-Token': shopifyAdminAccessToken,
      },
    }),
  })
}

/**
 * Gets headers for Shopify queries and mutations.
 */
const getShopifyHeaders = (locale: Locale) => {
  const shopifyCookies = getShopifyCookies(
    typeof document !== 'undefined' ? document.cookie : '',
  )
  const storefrontId = getLocaleVariable(
    locale,
    'NEXT_PUBLIC_SHOPIFY_STOREFRONT_ID',
  )

  // TODO: These headers are not verified to be correct for Shopify analytics.
  const shopifyHeaders: Record<string, string> = {
    [SHOPIFY_STOREFRONT_Y_HEADER]: shopifyCookies[SHOPIFY_Y],
    [SHOPIFY_STOREFRONT_S_HEADER]: shopifyCookies[SHOPIFY_S],
  }

  if (storefrontId) {
    shopifyHeaders[SHOPIFY_STOREFRONT_ID_HEADER] = storefrontId
  }

  return shopifyHeaders
}

/**
 * Queries a Shopify Storefront GraphQL endpoint.
 */
export const queryShopifyStorefront = async <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  locale: Locale,
  shopifyStorefrontGraphQlClient: ShopifyClient,
  query: DocumentNode,
  variables?: TVariables,
) => {
  const shopifyHeaders = getShopifyHeaders(locale)

  const result = await shopifyStorefrontGraphQlClient.query<TData>({
    query,
    variables,
    context: {
      headers: {
        ...shopifyHeaders,
      },
    },
  })

  return result
}

/**
 * Mutates a Shopify Storefront GraphQL endpoint.
 */
export const mutateShopifyStorefront = async <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  locale: Locale,
  shopifyStorefrontGraphQlClient: ShopifyClient,
  mutation: DocumentNode,
  variables?: TVariables,
) => {
  const shopifyHeaders = getShopifyHeaders(locale)

  const result = await shopifyStorefrontGraphQlClient.mutate<TData>({
    mutation,
    variables,
    context: {
      headers: {
        ...shopifyHeaders,
      },
    },
  })

  return result
}

/**
 * Queries a Shopify Admin GraphQL endpoint.
 */
export const queryShopifyAdmin = async <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  shopifyAdminGraphQlClient: ShopifyClient,
  query: DocumentNode,
  variables?: TVariables,
) => {
  const result = await shopifyAdminGraphQlClient.query<TData>({
    query,
    variables,
  })

  return result
}

/**
 * Mutates a Shopify Admin GraphQL endpoint.
 */
export const mutateShopifyAdmin = async <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  shopifyAdminGraphQlClient: ShopifyClient,
  mutation: DocumentNode,
  variables?: TVariables,
) => {
  const result = await shopifyAdminGraphQlClient.mutate<TData>({
    mutation,
    variables,
  })

  return result
}
